import type { NdjsonEvent, StructuredContent, ToolCall } from "./ndjsonTypes";

export const htmlToMarkdown = (html: string): string =>
  html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*<p>/gi, "\n\n")
    .replace(/<\/?p>/gi, "")
    .replace(/<b>(.*?)<\/b>/gi, "**$1**")
    .replace(/<strong>(.*?)<\/strong>/gi, "**$1**")
    .replace(/<i>(.*?)<\/i>/gi, "*$1*")
    .replace(/<em>(.*?)<\/em>/gi, "*$1*")
    .replace(/<[^>]+>/g, "")
    .trim();

export const stripEscalationJson = (text: string): string => {
  if (!text.startsWith("{")) {
    return text;
  }

  let depth = 0;
  let inString = false;
  let isEscaped = false;
  let jsonEnd = -1;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (isEscaped) {
      isEscaped = false;
      continue;
    }
    if (ch === "\\") {
      isEscaped = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (inString) {
      continue;
    }
    if (ch === "{") {
      depth++;
    } else if (ch === "}") {
      depth--;
      if (depth === 0) {
        jsonEnd = i + 1;
        break;
      }
    }
  }

  if (jsonEnd === -1) {
    return text;
  }

  const jsonStr = text.slice(0, jsonEnd);
  try {
    const parsed = JSON.parse(jsonStr) as Record<string, unknown>;
    if (!parsed.assistanceEventId) {
      return text;
    }
    const remaining = text.slice(jsonEnd).trim();
    const html =
      (parsed.htmlMessage as string) ?? (parsed.message as string) ?? "";
    const md = html ? htmlToMarkdown(html) : "";
    return [md, remaining].filter(Boolean).join("\n\n");
  } catch {
    return text;
  }
};

export const parseNdjsonLine = (line: string): NdjsonEvent | null => {
  const trimmed = line.trim();
  if (!trimmed.startsWith("{")) {
    return null;
  }
  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    if (
      parsed.type === "tool_call_start" ||
      parsed.type === "tool_call_end" ||
      parsed.type === "content"
    ) {
      return parsed as unknown as NdjsonEvent;
    }
    return null;
  } catch {
    return null;
  }
};

export const applyNdjsonEvent = (
  event: NdjsonEvent,
  fullText: string,
  toolCalls: ToolCall[]
): string => {
  switch (event.type) {
    case "tool_call_start":
      toolCalls.push({
        name: event.name,
        arguments: event.arguments,
        status: "running"
      });
      return fullText;
    case "tool_call_end": {
      const tc = toolCalls.find(
        (t) => t.name === event.name && t.status === "running"
      );
      if (tc) {
        tc.result = event.result;
        tc.status = "completed";
      }
      return fullText;
    }
    case "content":
      return fullText + event.data;
  }
};

export const parseStructuredContent = (
  contentType: string | undefined | null,
  contentValue: string | undefined | null
): StructuredContent => {
  const raw = contentValue ?? "";

  if (contentType !== "application/json") {
    return { text: stripEscalationJson(raw), toolCalls: [] };
  }

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const text = typeof parsed.text === "string" ? parsed.text : raw;
    const toolCalls = Array.isArray(parsed.toolCalls)
      ? (parsed.toolCalls as ToolCall[])
      : [];
    return { text, toolCalls };
  } catch {
    return { text: stripEscalationJson(raw), toolCalls: [] };
  }
};
